import { Eye, FileText, Mail, MessageSquare, Phone, Search, Settings, Users, Video } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCustomerCommunications } from '@/hooks/useCustomerCommunications';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { formatOutcomeLabel } from '@/lib/activity-type-maps';
import { formatDateTime } from '@/lib/utils';

interface Communication {
  id: string;
  customerId: string;
  customerName: string;
  contactName: string;
  type: 'email' | 'phone' | 'chat' | 'meeting' | 'sms' | 'video' | 'note' | 'portal';
  subject: string;
  content: string;
  status: 'sent' | 'delivered' | 'read' | 'replied' | 'completed' | 'failed';
  direction: 'inbound' | 'outbound';
  source?: string;
  createdAt: string;
  createdBy: string;
  recipientEmail?: string;
  recipientPhone?: string;
  durationMinutes?: number;
  outcome?: string;
  metadata?: {
    /** Recipient addresses recorded on live communications. */
    toRecipients?: string[];
    ccRecipients?: string[];
    /** Legacy/demo single-address fields. */
    emailTo?: string;
    emailFrom?: string;
    duration?: string;
    attendees?: string[];
    /** Invoice reference numbers of the documents attached to this communication. */
    invoiceReferences?: string[];
  };
}

const DEFAULT_COMMUNICATION_COLUMNS: ColumnDefinition[] = [
  { id: 'type', label: 'Type', visible: true, order: 0 },
  { id: 'subject', label: 'Subject', visible: true, order: 1 },
  { id: 'contact', label: 'Contact', visible: true, order: 2 },
  { id: 'status', label: 'Status', visible: true, order: 3 },
  { id: 'direction', label: 'Direction', visible: true, order: 4 },
  { id: 'source', label: 'Source', visible: true, order: 5 },
  { id: 'date', label: 'Date', visible: true, order: 6 },
  { id: 'createdBy', label: 'Created By', visible: true, order: 7 },
  { id: 'content', label: 'Content', visible: false, order: 8 },
];

interface CommunicationHistoryTabProps {
  customerId: string;
  customerName: string;
}

export function CommunicationHistoryTab({ customerId, customerName }: CommunicationHistoryTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [selectedCommunication, setSelectedCommunication] = useState<Communication | null>(null);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // Column preferences
  const {
    columns: commColumns,
    visibleColumns: commVisibleColumns,
    savePreferences: saveCommColumnPreferences,
    resetToDefaults: resetCommColumnPreferences,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.CustomerDetailCommunications,
    DEFAULT_COMMUNICATION_COLUMNS,
  );

  const itemsPerPage = 10;

  // Mode-aware communications query - uses mock data in demo mode, live API in live mode
  const { data: communications = [] } = useCustomerCommunications(
    customerId,
    sourceFilter !== 'all' ? sourceFilter : undefined,
  );

  // Communications from the hook, cast to local Communication type
  const customerCommunications = communications as Communication[];

  // Apply filters
  const filteredCommunications = useMemo(() => {
    return customerCommunications.filter((comm) => {
      const matchesSearch =
        (comm.subject || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (comm.content || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (comm.contactName || '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = typeFilter === 'all' || comm.type === typeFilter;

      // Date range filter
      let matchesDateRange = true;
      if (dateRangeFilter !== 'all') {
        const commDate = new Date(comm.createdAt);
        const today = new Date();
        const daysDiff = Math.floor((today.getTime() - commDate.getTime()) / (1000 * 60 * 60 * 24));

        switch (dateRangeFilter) {
          case '7days':
            matchesDateRange = daysDiff <= 7;
            break;
          case '30days':
            matchesDateRange = daysDiff <= 30;
            break;
          case '90days':
            matchesDateRange = daysDiff <= 90;
            break;
        }
      }

      return matchesSearch && matchesType && matchesDateRange;
    });
  }, [customerCommunications, searchQuery, typeFilter, dateRangeFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredCommunications.length / itemsPerPage);
  const paginatedCommunications = filteredCommunications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Get icon for communication type
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'phone':
        return <Phone className="h-4 w-4" />;
      case 'chat':
        return <MessageSquare className="h-4 w-4" />;
      case 'meeting':
        return <Users className="h-4 w-4" />;
      case 'sms':
        return <MessageSquare className="h-4 w-4" />;
      case 'video':
        return <Video className="h-4 w-4" />;
      case 'note':
        return <FileText className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  // Get badge color for type
  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'email':
        return 'bg-blue-500 text-white hover:bg-blue-600';
      case 'phone':
        return 'bg-green-500 text-white hover:bg-green-600';
      case 'chat':
        return 'bg-purple-500 text-white hover:bg-purple-600';
      case 'meeting':
        return 'bg-orange-500 text-white hover:bg-orange-600';
      case 'sms':
        return 'bg-cyan-500 text-white hover:bg-cyan-600';
      case 'video':
        return 'bg-pink-500 text-white hover:bg-pink-600';
      case 'note':
        return 'bg-gray-500 text-white hover:bg-gray-600';
      default:
        return 'bg-gray-500 text-white hover:bg-gray-600';
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'secondary';
      case 'read':
      case 'replied':
      case 'completed':
        return 'default';
      case 'failed':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  // Handle view details
  const handleViewDetails = (communication: Communication) => {
    setSelectedCommunication(communication);
    setViewDetailsDialog(true);
  };

  // Render cell content based on column ID
  const renderCellContent = (columnId: string, comm: Communication) => {
    switch (columnId) {
      case 'type':
        return (
          <Badge className={getTypeBadgeColor(comm.type)}>
            <span className="mr-1">{getTypeIcon(comm.type)}</span>
            {comm.type}
          </Badge>
        );
      case 'subject':
        return <span className="font-medium">{comm.subject}</span>;
      case 'contact':
        return comm.contactName;
      case 'status':
        return <Badge variant={getStatusBadgeVariant(comm.status)}>{comm.status}</Badge>;
      case 'direction':
        return <Badge variant={comm.direction === 'inbound' ? 'outline' : 'secondary'}>{comm.direction}</Badge>;
      case 'source':
        return comm.source ? <Badge variant="outline">{comm.source}</Badge> : null;
      case 'date':
        return formatDateTime(comm.createdAt);
      case 'createdBy':
        return comm.createdBy;
      case 'content':
        return (
          <span className="truncate max-w-[200px] block" title={comm.content}>
            {comm.content}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex flex-col">
            <CardTitle className="text-base font-medium">Communication History for {customerName}</CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-1 ml-4 justify-end">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search communications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="chat">Chat</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="note">Note</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="Dispute">Disputes</SelectItem>
                <SelectItem value="Promise">Promise</SelectItem>
                <SelectItem value="Case">Collection Case</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setColumnConfigOpen(true)}
                    aria-label="Configure Columns"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configure Columns</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent>
          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {commVisibleColumns.map((column) => (
                    <TableHead key={column.id}>{column.label}</TableHead>
                  ))}
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCommunications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={commVisibleColumns.length + 1} className="text-center text-muted-foreground">
                      No communications found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCommunications.map((comm) => (
                    <TableRow key={comm.id}>
                      {commVisibleColumns.map((column) => (
                        <TableCell key={column.id}>{renderCellContent(column.id, comm)}</TableCell>
                      ))}
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetails(comm)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, filteredCommunications.length)} of {filteredCommunications.length}{' '}
                communications
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Column Config Dialog */}
      <ColumnConfigDialog
        columns={commColumns}
        onApply={saveCommColumnPreferences}
        onReset={resetCommColumnPreferences}
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
      />

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialog} onOpenChange={setViewDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedCommunication?.subject || 'Communication Details'}</DialogTitle>
            <DialogDescription>Communication ID: {selectedCommunication?.id}</DialogDescription>
          </DialogHeader>
          {selectedCommunication && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <div className="mt-1">
                    <Badge className={getTypeBadgeColor(selectedCommunication.type)}>
                      <span className="mr-1">{getTypeIcon(selectedCommunication.type)}</span>
                      {selectedCommunication.type}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge variant={getStatusBadgeVariant(selectedCommunication.status)}>
                      {selectedCommunication.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Direction</Label>
                  <div className="mt-1">
                    <Badge variant={selectedCommunication.direction === 'inbound' ? 'outline' : 'secondary'}>
                      {selectedCommunication.direction}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Source</Label>
                  <div className="mt-1">
                    {selectedCommunication.source ? (
                      <Badge variant="outline">{selectedCommunication.source}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Customer</Label>
                  <p className="mt-1 font-medium">{selectedCommunication.customerName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Contact</Label>
                  <p className="mt-1 font-medium">
                    {selectedCommunication.contactName || '-'}
                    {selectedCommunication.recipientPhone && (
                      <span className="text-muted-foreground text-sm ml-1">
                        ({selectedCommunication.recipientPhone})
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created By</Label>
                  <p className="mt-1 font-medium">{selectedCommunication.createdBy}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date</Label>
                  <p className="mt-1 font-medium">{formatDateTime(selectedCommunication.createdAt)}</p>
                </div>
              </div>

              {selectedCommunication.type === 'phone' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Outcome</Label>
                    <p className="mt-1 font-medium">
                      {selectedCommunication.outcome ? formatOutcomeLabel(selectedCommunication.outcome) : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Duration</Label>
                    <p className="mt-1 font-medium">
                      {selectedCommunication.durationMinutes != null
                        ? `${selectedCommunication.durationMinutes} min`
                        : '-'}
                    </p>
                  </div>
                </div>
              )}

              {selectedCommunication.type === 'email' &&
                (() => {
                  const meta = selectedCommunication.metadata;
                  const emailTo =
                    meta?.toRecipients?.join(', ') || meta?.emailTo || selectedCommunication.recipientEmail;
                  const emailCc = meta?.ccRecipients?.join(', ');
                  const emailFrom = meta?.emailFrom;
                  const invoices = meta?.invoiceReferences?.length ? meta.invoiceReferences.join(', ') : undefined;

                  if (!emailTo && !emailCc && !emailFrom && !invoices) {
                    return null;
                  }

                  return (
                    <div>
                      <Label className="text-muted-foreground">Email Details</Label>
                      <div className="mt-1 space-y-1 rounded-md border bg-muted/50 p-4 text-sm">
                        {emailTo && (
                          <div>
                            <span className="font-medium">To:</span> {emailTo}
                          </div>
                        )}
                        {emailCc && (
                          <div>
                            <span className="font-medium">CC:</span> {emailCc}
                          </div>
                        )}
                        {emailFrom && (
                          <div>
                            <span className="font-medium">From:</span> {emailFrom}
                          </div>
                        )}
                        {invoices && (
                          <div>
                            <span className="font-medium">Invoices:</span> {invoices}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

              <div>
                <Label className="text-muted-foreground">Notes</Label>
                <div className="mt-1 rounded-md border bg-muted/50 p-4 min-h-[120px]">
                  {selectedCommunication.type === 'email' ? (
                    <iframe
                      srcDoc={selectedCommunication.content}
                      className="w-full min-h-[200px] border-0"
                      sandbox=""
                      title="Email content"
                    />
                  ) : (
                    <p className="whitespace-pre-wrap">{selectedCommunication.content}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
